<template>
  <div class="!w-[610px]" v-show="progressData && showGameplayModule">
    <!-- ==================== 玩法模块（仅已登录且已报名用户显示） ==================== -->
    <!-- 灰色统计模块 -->
    <ProgressStats
      v-if="props.showProgressStats !== false"
      class="mb-[30px]"
      :today-view-count="articleCheckinStats.todayViewCount"
      :total-count="articleCheckinStats.totalCount"
      :total-duration-seconds="articleCheckinStats.totalDurationSeconds"
    />
    <!-- 分割线（仅统计模块显示时才展示） -->
    <div v-if="props.showProgressStats !== false" class="w-full h-[1px] bg-black/20 mb-[30px]" />

    <!-- 标题文字 -->
    <div class="mb-[30px] w-full flex flex-col items-start">
      <!-- 自定义主标题（运营通过 form-config 配置，fc-textEditor 输出 HTML） -->
      <div
        v-if="hasCustomTitlePrefix"
        :class="
          isEnglish
            ? 'font-br-hendrix text-[28px] leading-[130%] tracking-[-1px] align-middle text-[#161616]'
            : 'font-normal leading-[140%] tracking-[0.5px] text-[#161616] text-[30px]'
        "
        v-html="titlePrefixText"
      />
      <!-- 默认主标题（前缀 + 高亮词） -->
      <div
        v-else
        :class="
          isEnglish
            ? 'font-br-hendrix text-[28px] leading-[130%] tracking-[-1px] align-middle text-[#161616]'
            : 'font-normal leading-[140%] tracking-[0.5px] text-[#161616] text-[30px]'
        "
      >
        {{ titlePrefixText
        }}<span
          v-if="titleHighlightText"
          :class="
            isEnglish
              ? 'font-br-hendrix font-medium text-[28px] leading-[130%] tracking-[-1px] align-middle text-[#FF6800]'
              : 'text-[#FF6800] text-[30px]'
          "
          >{{ titleHighlightText }}</span
        >
      </div>
      <!-- 副标题：未完成所有里程碑时显示下一个里程碑的进度 -->
      <div
        v-if="
          hasMilestoneRewardConfig &&
          !allMilestonesCompleted &&
          nextMilestoneByMax &&
          daysToNextMilestoneByMax > 0
        "
        :class="
          isEnglish
            ? 'font-br-hendrix font-normal text-[23px] leading-[100%] tracking-[-0.025em] text-[#666] mt-[8px]'
            : 'mt-[8px] font-normal leading-[100%] text-[#666] text-[24px]'
        "
        v-html="formatSubtitle(daysToNextMilestoneByMax, nextMilestoneByMax.reward, nextMilestoneByMax.rewardName)"
      />
      <!-- 副标题：完成所有里程碑时显示祝贺文案（有奖励金额时才展示总金额） -->
      <div
        v-else-if="hasMilestoneRewardConfig && allMilestonesCompleted"
        :class="
          isEnglish
            ? 'font-br-hendrix font-normal text-[23px] leading-[100%] tracking-[-0.025em] text-[#666] mt-[8px] whitespace-pre-line'
            : 'mt-[8px] font-normal leading-[100%] text-[#666] text-[24px]'
        "
        v-html="formatAllCompleted(totalMilestoneReward)"
      />
    </div>

    <!-- 里程碑进度条（需要 B 端配置里程碑奖励才展示） -->
    <MilestoneProgress
      v-if="hasMilestoneRewardConfig"
      class="mb-[40px]"
      :milestones="milestones"
      :current-streak="displayStreak"
      :currency="milestoneCurrency"
    />
  </div>
</template>

<script lang="ts">
/**
 * 里程碑进度组件（纯展示）
 *
 * 职责：
 * 1. 纯展示组件，不负责数据获取
 * 2. 通过 watch 监听 Fego 变量 checkin_progress_data 获取数据
 * 3. 数据由物料展示组件（material-list）通过 updateFegoVariable 提供
 *
 * 监听的 Fego 变量：
 * - checkin_progress_data: 里程碑进度所需的完整数据
 *   {
 *     actPlaybookId: string,
 *     showGameplayModule: boolean,
 *     hasMilestoneRewardConfig: boolean,
 *     milestoneRewards: MilestoneRewardItem[],
 *     milestoneRewardCurrency: keyof typeof CurrencyEnum.KV,
 *     tasks: TaskSnapshot[],
 *     dailyCheckinStats: DailyCheckinStats,
 *     articleCheckinStats: ArticleCheckinStats,
 *     isEnglish: boolean,
 *   }
 */
import { computed, ref, watch } from 'vue-demi';
import type { MilestoneRewardItem } from '@ark/material-checkin-shared';
import { lang } from './utils/lang';
import $t from './utils/i18n';
import { ELang } from '@ark/utils';
import { formatRewardAmount } from './utils/format-reward';
import { CurrencyEnum } from '@ark/utils';

// 组件导入
import ProgressStats from './components/ProgressStats.vue';
import MilestoneProgress from './components/MilestoneProgress.vue';

// Hooks 工具函数导入
import { type TaskSnapshot, type DailyCheckinStats, type ArticleCheckinStats, type DailyCheckinTaskProgress,
// 里程碑 hook
useMilestones } from './hooks';

// ==================== form-config props ====================

// ==================== Fego 变量数据结构 ====================

interface CheckinProgressData {
  actPlaybookId: string;
  showGameplayModule: boolean;
  hasMilestoneRewardConfig: boolean;
  milestoneRewards: MilestoneRewardItem[];
  milestoneCurrency: keyof typeof CurrencyEnum.KV;
  tasks: TaskSnapshot[];
  dailyCheckinStats: DailyCheckinStats;
  articleCheckinStats: {
    todayViewCount: number;
    totalCount: number;
    totalDurationSeconds: number;
    todayViewedMaterialIds: string[];
  };
  isEnglish: boolean;
  /** 任务维度打卡进度（累计模式专用，旧活动不传此字段） */
  taskProgress?: DailyCheckinTaskProgress;
}

// ==================== 核心状态（从 Fego 变量获取） ====================
const __sfc_main = {};
__sfc_main.props = {
  showProgressStats: {
    key: "showProgressStats",
    required: true,
    type: Boolean
  },
  titlePrefix: {
    key: "titlePrefix",
    required: false,
    type: String
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const progressData = ref<CheckinProgressData | null>(null);
  const isEnglish = computed(() => progressData.value?.isEnglish ?? lang === ELang.KV.EN);

  // 是否显示玩法模块
  const showGameplayModule = computed(() => progressData.value?.showGameplayModule ?? false);

  // 是否有里程碑奖励配置
  const hasMilestoneRewardConfig = computed(() => progressData.value?.hasMilestoneRewardConfig ?? false);

  // 里程碑奖励配置
  const milestoneRewards = ref<MilestoneRewardItem[]>([]);
  const milestoneCurrency = ref<keyof typeof CurrencyEnum.KV>(CurrencyEnum.KV.USD);

  // 任务配置
  const tasks = ref<TaskSnapshot[]>([]);

  // 打卡统计状态
  const dailyCheckinStats = ref<DailyCheckinStats>({
    maxStreak: 0,
    currentStreak: 0,
    checkinDates: []
  });
  const articleCheckinStats = ref<ArticleCheckinStats>({
    todayViewCount: 0,
    totalCount: 0,
    totalDurationSeconds: 0,
    todayViewedMaterialIds: new Set()
  });

  // 任务维度打卡进度（累计模式专用，旧活动无此字段时为 null）
  const taskProgress = ref<DailyCheckinTaskProgress | null>(null);

  // ==================== 语言与文案 ====================

  // 是否使用运营配置的自定义主标题（fc-textEditor 输出 HTML）
  const hasCustomTitlePrefix = computed(() => !!props.titlePrefix);
  const titlePrefixText = computed(() => {
    return props.titlePrefix || $t('CheckinProgress.titlePrefix');
  });
  const titleHighlightText = computed(() => $t('CheckinProgress.titleHighlight'));
  function formatSubtitle(days: number, reward: number | undefined, rewardName?: string): string {
    const hasReward = reward != null && reward > 0;
    if (!hasReward) {
      // 无奖励金额时：用 rewardName 直接填充（后台已处理为非富文本）
      const template = $t('CheckinProgress.subtitleNoRewardTemplate');
      return template.replace('{days}', String(days)).replace('{rewardName}', rewardName || '');
    }
    const template = $t('CheckinProgress.subtitleTemplate');
    return template.replace('{days}', String(days)).replace('{rewardWithCurrency}', formatRewardAmount(milestoneCurrency.value, reward));
  }
  function formatAllCompleted(totalReward: number): string {
    if (totalReward <= 0) {
      return $t('CheckinProgress.allCompletedTemplateNoReward');
    }
    return $t('CheckinProgress.allCompletedTemplate').replace('{totalRewardWithCurrency}', formatRewardAmount(milestoneCurrency.value, totalReward));
  }

  // ==================== Hook: 里程碑 ====================

  const {
    milestones,
    displayStreak
  } = useMilestones({
    tasks,
    milestoneRewards,
    dailyCheckinStats,
    taskProgress,
    isEnglish
  });

  // 使用历史最大连续天数，避免断连导致文案回退
  const maxStreak = computed(() => dailyCheckinStats.value.maxStreak);
  const nextMilestoneByMax = computed(() => {
    if (!milestones.value.length) return null;
    // 基于最大连续天数计算“下一个里程碑”
    const next = milestones.value.find(m => m.days > maxStreak.value);
    return next || milestones.value[milestones.value.length - 1];
  });
  const daysToNextMilestoneByMax = computed(() => {
    if (!nextMilestoneByMax.value) return 0;
    // 剩余天数 = 目标天数 - 当前连续天数（断连时仍按当前进度计算）
    const remaining = nextMilestoneByMax.value.days - displayStreak.value;
    return remaining > 0 ? remaining : 0;
  });

  // 是否完成所有里程碑（以最大连续天数判断）
  const allMilestonesCompleted = computed(() => {
    if (!milestones.value.length) return false;
    const currentStreak = dailyCheckinStats.value.maxStreak;
    const lastMilestone = milestones.value[milestones.value.length - 1];
    return currentStreak >= lastMilestone.days;
  });

  // 所有里程碑的总奖励金额（无奖励配置的里程碑 reward 为 undefined，跳过累加）
  const totalMilestoneReward = computed(() => {
    return milestones.value.reduce((sum, m) => sum + (m.reward ?? 0), 0);
  });

  // ==================== 监听 Fego 变量 ====================

  watch(() => (window as any).FEGORender.store.variable.data.checkin_progress_data, newData => {
    if (newData) {
      const data = newData as CheckinProgressData;
      progressData.value = data;
      console.log('[里程碑进度] Fego变量变化', data);

      // 更新本地状态
      milestoneRewards.value = data.milestoneRewards || [];
      milestoneCurrency.value = data.milestoneCurrency;
      tasks.value = data.tasks || [];
      dailyCheckinStats.value = data.dailyCheckinStats || {
        maxStreak: 0,
        currentStreak: 0,
        checkinDates: []
      };

      // 转换 articleCheckinStats（将数组转回 Set）
      const todayViewedIds = data.articleCheckinStats?.todayViewedMaterialIds || [];
      articleCheckinStats.value = {
        todayViewCount: data.articleCheckinStats?.todayViewCount || 0,
        totalCount: data.articleCheckinStats?.totalCount || 0,
        totalDurationSeconds: data.articleCheckinStats?.totalDurationSeconds || 0,
        todayViewedMaterialIds: new Set(todayViewedIds)
      };

      // 更新任务维度打卡进度（旧活动不传此字段时降级为 null）
      taskProgress.value = data.taskProgress ?? null;
      console.log('[里程碑进度] 断连文案计算', {
        currentStreak: dailyCheckinStats.value.currentStreak,
        maxStreak: dailyCheckinStats.value.maxStreak,
        nextMilestoneDays: nextMilestoneByMax.value?.days,
        daysToNextMilestone: daysToNextMilestoneByMax.value
      });
    }
  }, {
    deep: true,
    immediate: true
  });
  return {
    props,
    progressData,
    isEnglish,
    showGameplayModule,
    hasMilestoneRewardConfig,
    milestoneCurrency,
    articleCheckinStats,
    hasCustomTitlePrefix,
    titlePrefixText,
    titleHighlightText,
    formatSubtitle,
    formatAllCompleted,
    milestones,
    displayStreak,
    nextMilestoneByMax,
    daysToNextMilestoneByMax,
    allMilestonesCompleted,
    totalMilestoneReward
  };
};
__sfc_main.components = Object.assign({
  ProgressStats,
  MilestoneProgress
}, __sfc_main.components);
export default __sfc_main;
</script>

<style>
@tailwind base;
@tailwind components;
@tailwind utilities;
</style>

<style scoped>
/* BR Hendrix 字体 */
.font-br-hendrix {
  font-family: 'BR Hendrix', sans-serif;
}
</style>
